import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import { Analytics } from '@vercel/analytics/react';
import TracksyApp from './App';
import {
  auth,
  loginWithEmail,
  signupWithEmail,
  loginWithGoogle,
  logout,
  resetPassword,
  getUserData,
  saveUserData,
  onAuthStateChanged,
  getExemptEmails,
  getVoiceMemos,
  setPlanInFirestore,
} from './firebase';
import AppRouter from './AppRouter';
import { getStoredReferralCode, recordReferral, validateAffiliateCode } from './utils/referral';

// ─── AUTH CSS ──────────────────────────────────────────────────────────────
const AUTH_CSS = `
  .auth-container {
    min-height:100vh; display:flex; align-items:center; justify-content:center;
    background:#0f0f12; padding:20px; font-family:-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif;
  }
  .auth-card {
    width:100%; max-width:400px; background:rgba(255,255,255,.04);
    border:1px solid rgba(255,255,255,.09); border-radius:24px; padding:40px 32px;
  }
  .auth-logo {
    text-align:center; font-size:28px; font-weight:900; letter-spacing:1px;
    color:#fff; -webkit-text-stroke:1.5px #00ff99;
    text-shadow:3px 3px 0 rgba(0,0,0,.3),0 0 10px rgba(0,255,136,.5);
    margin-bottom:8px;
  }
  .auth-subtitle { text-align:center; color:rgba(255,255,255,.5); font-size:14px; margin-bottom:32px; }
  .auth-form { display:flex; flex-direction:column; gap:16px; }
  .auth-input {
    background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.12);
    border-radius:12px; padding:14px 16px; color:#fff; font-size:15px;
    font-family:inherit; width:100%; box-sizing:border-box; outline:none;
    transition:border-color .2s;
  }
  .auth-input:focus { border-color:#00ff99; background:rgba(255,255,255,.09); }
  .auth-input::placeholder { color:rgba(255,255,255,.3); }
  .auth-pw-wrap {
    position:relative; display:flex; align-items:center;
  }
  .auth-pw-wrap .auth-input {
    padding-right:48px;
  }
  .auth-pw-toggle {
    position:absolute; right:12px; top:50%; transform:translateY(-50%);
    background:none; border:none; cursor:pointer; padding:6px;
    color:rgba(255,255,255,.5); display:flex; align-items:center;
    justify-content:center; transition:color .2s;
  }
  .auth-pw-toggle:hover { color:rgba(255,255,255,.8); }
  .auth-pw-toggle svg { width:22px; height:22px; stroke-width:2.5; filter:drop-shadow(0 0 2px rgba(0,0,0,.8)); }
  .auth-btn {
    background:linear-gradient(135deg,#00ff99,#00e5ff); border:none;
    color:#000; padding:14px; border-radius:12px; font-size:16px;
    font-weight:700; cursor:pointer; transition:.2s ease; font-family:inherit;
  }
  .auth-btn:hover { transform:scale(1.02); }
  .auth-btn:disabled { opacity:.5; cursor:not-allowed; transform:none; }
  .auth-divider {
    display:flex; align-items:center; gap:12px; margin:4px 0;
    color:rgba(255,255,255,.3); font-size:13px;
  }
  .auth-divider::before, .auth-divider::after {
    content:''; flex:1; height:1px; background:rgba(255,255,255,.1);
  }
  .auth-google {
    display:flex; align-items:center; justify-content:center; gap:10px;
    background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.15);
    color:#fff; padding:14px; border-radius:12px; font-size:15px;
    font-weight:600; cursor:pointer; transition:.2s ease; width:100%;
    font-family:inherit;
  }
  .auth-google:hover { background:rgba(255,255,255,.12); }
  .auth-google svg { width:20px; height:20px; }
  .auth-switch {
    text-align:center; color:rgba(255,255,255,.5); font-size:14px; margin-top:8px;
  }
  .auth-switch a {
    color:#00ff99; cursor:pointer; text-decoration:none; font-weight:600;
  }
  .auth-switch a:hover { text-decoration:underline; }
  .auth-error {
    background:rgba(255,77,77,.1); border:1px solid rgba(255,77,77,.2);
    color:#ff6b6b; padding:12px 16px; border-radius:10px; font-size:13px;
    text-align:center;
  }
  .auth-success {
    background:rgba(0,255,136,.1); border:1px solid rgba(0,255,136,.2);
    color:#00ff99; padding:12px 16px; border-radius:10px; font-size:13px;
    text-align:center;
  }
  .auth-forgot {
    text-align:right; color:rgba(255,255,255,.4); font-size:13px;
    cursor:pointer; margin-top:-8px;
  }
  .auth-forgot:hover { color:#00ff99; }
  .ref-modal-overlay {
    position:fixed; inset:0; background:rgba(0,0,0,.7); display:flex;
    align-items:center; justify-content:center; z-index:9999; padding:20px;
    animation:refFadeIn .25s ease;
  }
  @keyframes refFadeIn { from { opacity:0; } to { opacity:1; } }
  .ref-modal {
    width:100%; max-width:380px; background:#1a1a22;
    border:1px solid rgba(255,255,255,.12); border-radius:20px; padding:32px 28px;
    text-align:center;
  }
  .ref-modal-emoji { font-size:40px; margin-bottom:12px; }
  .ref-modal h3 { color:#fff; font-size:18px; font-weight:700; margin:0 0 8px; }
  .ref-modal p { color:rgba(255,255,255,.5); font-size:14px; margin:0 0 20px; line-height:1.4; }
  .ref-modal input {
    width:100%; box-sizing:border-box; background:rgba(255,255,255,.06);
    border:1px solid rgba(255,255,255,.12); border-radius:12px; padding:14px 16px;
    color:#fff; font-size:15px; font-family:inherit; outline:none;
    letter-spacing:1.5px; text-align:center; transition:border-color .2s;
  }
  .ref-modal input:focus { border-color:#00ff99; background:rgba(255,255,255,.09); }
  .ref-modal input::placeholder { color:rgba(255,255,255,.3); letter-spacing:normal; }
  .ref-modal-btns { display:flex; gap:10px; margin-top:16px; }
  .ref-modal-btns button {
    flex:1; padding:12px; border-radius:12px; font-size:15px;
    font-weight:600; cursor:pointer; font-family:inherit; border:none; transition:.2s ease;
  }
  .ref-modal-skip {
    background:rgba(255,255,255,.08); color:rgba(255,255,255,.6);
  }
  .ref-modal-skip:hover { background:rgba(255,255,255,.12); color:#fff; }
  .ref-modal-submit {
    background:linear-gradient(135deg,#00ff99,#00e5ff); color:#000; font-weight:700;
  }
  .ref-modal-submit:hover { transform:scale(1.02); }
  .ref-modal-submit:disabled { opacity:.5; cursor:not-allowed; transform:none; }
  .ref-modal-error {
    background: rgba(255,77,77,.1); border: 1px solid rgba(255,77,77,.3);
    color: #ff6b6b; padding: 10px 14px; border-radius: 8px;
    font-size: 13px; text-align: center; margin-bottom: 8px;
  }
  .ref-modal-applied {
    background: rgba(0,255,136,.08); border: 1px solid rgba(0,255,136,.2);
    color: #00ff88; padding: 10px 14px; border-radius: 8px;
    font-size: 13px; text-align: center; margin-bottom: 8px; font-weight: 600;
  }
  .auth-loading {
    min-height:100vh; display:flex; align-items:center; justify-content:center;
    background:#0f0f12; color:#00ff99; font-size:18px; font-weight:700;
    font-family:-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif;
    flex-direction:column; gap:16px;
  }
  .auth-spinner {
    width:40px; height:40px; border:3px solid rgba(0,255,136,.2);
    border-top-color:#00ff99; border-radius:50%;
    animation:authSpin .8s linear infinite;
  }
  @keyframes authSpin { to { transform:rotate(360deg); } }
`;

// ─── EYE ICONS ──────────────────────────────────────────────────────────────
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

// ─── GOOGLE ICON ────────────────────────────────────────────────────────────
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// ─── DEFAULT DATA ───────────────────────────────────────────────────────────
const getDefaultData = (name = '') => ({
  settings: {
    name, dealership: '', role: 'Salesperson',
    unitsGoal: 20, grossGoal: 8000, drawMonthly: 2500,
    commissionMode: 'split', frontPct: 8, backPct: 15, bothPct: 15,
    newUnitBonus: 150, usedUnitBonus: 100,
    callsTarget: 12, textsTarget: 4, emailsTarget: 8, useAutoTargets: true,
  },
  sales: [], activities: [],
  bonusTiers: [
    { id: 'T1', unitsSold: 10, bonusAmount: 500 },
    { id: 'T2', unitsSold: 15, bonusAmount: 1000 },
    { id: 'T3', unitsSold: 20, bonusAmount: 1500 },
    { id: 'T4', unitsSold: 25, bonusAmount: 2000 },
    { id: 'T5', unitsSold: 30, bonusAmount: 2500 },
  ],
  voiceMemos: [],
  leads: [],
  appointments: [],
});

const getCache = (uid) => { try { return JSON.parse(localStorage.getItem(`tracksy_${uid}`)); } catch(e) { return null; } };
const setCache = (uid, data) => { try { localStorage.setItem(`tracksy_${uid}`, JSON.stringify(data)); } catch(e) {} };

// ─── AUTH SCREEN ────────────────────────────────────────────────────────────
function AuthScreen({ onLogin }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      if (mode === 'reset') {
        await resetPassword(email);
        setSuccess('Password reset email sent! Check your inbox.');
        setLoading(false);
        return;
      }
      if (mode === 'signup') {
        if (password !== confirmPassword) { setError('Passwords do not match'); setLoading(false); return; }
        if (password.length < 6) { setError('Password must be at least 6 characters'); setLoading(false); return; }
        const r = await signupWithEmail(email, password);
        onLogin(r.user, referralCode.trim());
      } else {
        const r = await loginWithEmail(email, password);
        onLogin(r.user);
      }
    } catch (err) {
      const m = {
        'auth/invalid-credential': 'Invalid email or password',
        'auth/user-not-found': 'No account found with this email',
        'auth/wrong-password': 'Incorrect password',
        'auth/email-already-in-use': 'An account already exists with this email',
        'auth/weak-password': 'Password must be at least 6 characters',
        'auth/invalid-email': 'Please enter a valid email address',
        'auth/too-many-requests': 'Too many attempts. Please try again later',
      };
      setError(m[err.code] || err.message);
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError(''); setLoading(true); setGoogleLoading(true);
    try {
      const r = await loginWithGoogle();
      // Pass typed code if non-empty; otherwise undefined so handleLogin shows modal for new users
      onLogin(r.user, referralCode.trim() || undefined);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') setError('Google sign-in failed. Please try again.');
      setLoading(false); setGoogleLoading(false);
    }
  };

  // Reset visibility when switching modes
  useEffect(() => {
    setShowPassword(false);
    setShowConfirmPassword(false);
  }, [mode]);

  return (
    <>
      <style>{AUTH_CSS}</style>
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo">TRACKSY</div>
          <div className="auth-subtitle">
            {mode === 'login' && 'Sign in to your account'}
            {mode === 'signup' && 'Create your free account'}
            {mode === 'reset' && 'Reset your password'}
          </div>
          {error && <div className="auth-error">{error}</div>}
          {success && <div className="auth-success">{success}</div>}
          <form className="auth-form" onSubmit={handleEmailAuth}>
            <input className="auth-input" type="email" placeholder="Email address"
              value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
            {mode !== 'reset' && (
              <div className="auth-pw-wrap">
                <input className="auth-input" type={showPassword ? 'text' : 'password'} placeholder="Password"
                  value={password} onChange={e => setPassword(e.target.value)} required
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} />
                <button type="button" className="auth-pw-toggle" onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            )}
            {mode === 'signup' && (
              <div className="auth-pw-wrap">
                <input className="auth-input" type={showConfirmPassword ? 'text' : 'password'} placeholder="Confirm password"
                  value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  required autoComplete="new-password" />
                <button type="button" className="auth-pw-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1} aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                  {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            )}
            {mode === 'signup' && (
              <input className="auth-input" type="text" placeholder="Referral code (optional) e.g. BEAR26"
                value={referralCode}
                onChange={e => setReferralCode(e.target.value.toUpperCase())}
                autoComplete="off"
                style={{letterSpacing:'1.5px'}} />
            )}
            {mode === 'login' && (
              <div className="auth-forgot" onClick={() => { setMode('reset'); setError(''); setSuccess(''); }}>Forgot password?</div>
            )}
            <button className="auth-btn" type="submit" disabled={loading}>
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Send Reset Email'}
            </button>
          </form>
          {mode !== 'reset' && (
            <>
              <div className="auth-divider">or</div>
              <button className="auth-google" onClick={handleGoogle} disabled={loading}>
                {googleLoading ? <><div className="auth-spinner" style={{width:20,height:20,borderWidth:2}} /> Signing in with Google...</> : <><GoogleIcon /> Continue with Google</>}
              </button>
            </>
          )}
          <div className="auth-switch">
            {mode === 'login' && <>Don't have an account? <a onClick={() => { setMode('signup'); setError(''); setSuccess(''); }}>Sign up</a></>}
            {mode === 'signup' && <>Already have an account? <a onClick={() => { setMode('login'); setError(''); setSuccess(''); }}>Sign in</a></>}
            {mode === 'reset' && <>Remember your password? <a onClick={() => { setMode('login'); setError(''); setSuccess(''); }}>Sign in</a></>}
          </div>
          {mode === 'signup' && (
            <div style={{textAlign:'center',fontSize:12,color:'rgba(255,255,255,.35)',marginTop:12,lineHeight:1.6}}>
              By signing up you agree to our{' '}
              <a href="/terms" style={{color:'rgba(255,255,255,.55)',textDecoration:'underline'}}>Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" style={{color:'rgba(255,255,255,.55)',textDecoration:'underline'}}>Privacy Policy</a>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── SUBSCRIPTION HELPERS ───────────────────────────────────────────────────
const getSubCache = (uid) => { try { return JSON.parse(localStorage.getItem(`tracksy_sub_${uid}`)); } catch(e) { return null; } };
const setSubCache = (uid, data) => { try { localStorage.setItem(`tracksy_sub_${uid}`, JSON.stringify({ ...data, cachedAt: Date.now() })); } catch(e) {} };

const checkSubscription = async (email) => {
  try {
    const res = await fetch('/api/stripe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'check-subscription', email })
    });
    return await res.json();
  } catch(e) { return { active: false, status: 'error' }; }
};

const createCheckout = async (email, uid, targetPlan = 'core', billingCycle = 'monthly', referralCode = '') => {
  const res = await fetch('/api/stripe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'create-checkout', email, uid, targetPlan, billingCycle, referralCode })
  });
  const data = await res.json();
  if (data.error === 'already_subscribed') {
    const sub = await checkSubscription(email);
    if (sub.active) setSubCache(uid, sub);
    window.location.reload();
    return;
  }
  if (data.url) window.location.href = data.url;
};

const openCustomerPortal = async (email) => {
  const res = await fetch('/api/stripe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'create-portal', email })
  });
  const data = await res.json();
  if (data.url) window.location.href = data.url;
};

// ─── PLAN SELECTION SCREEN ───────────────────────────────────────────────────
const PLAN_CSS = `
  .ps-container {
    min-height:100vh; background:#0f0f12; padding:20px 16px 40px;
    font-family:-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif;
    display:flex; flex-direction:column; align-items:center;
  }
  .ps-logo {
    font-size:26px; font-weight:900; letter-spacing:1px; color:#fff;
    -webkit-text-stroke:1.5px #00ff99;
    text-shadow:3px 3px 0 rgba(0,0,0,.3),0 0 10px rgba(0,255,136,.5);
    margin:24px 0 6px;
  }
  .ps-tagline { color:rgba(255,255,255,.45); font-size:13px; margin-bottom:28px; }
  .ps-status-warn {
    background:rgba(255,200,0,.1); border:1px solid rgba(255,200,0,.2);
    color:#ffc800; padding:10px 16px; border-radius:10px; font-size:13px;
    margin-bottom:20px; text-align:center; max-width:480px; width:100%;
  }
  .ps-cards { display:flex; gap:14px; width:100%; max-width:520px; flex-wrap:wrap; }
  .ps-card {
    flex:1; min-width:220px; background:rgba(255,255,255,.04);
    border:1.5px solid rgba(255,255,255,.1); border-radius:20px;
    padding:24px 20px; display:flex; flex-direction:column;
    position:relative; transition:border-color .2s;
  }
  .ps-card.ps-featured { border-color:rgba(0,255,136,.4); }
  .ps-card-badge {
    position:absolute; top:-11px; left:50%; transform:translateX(-50%);
    background:linear-gradient(135deg,#00ff99,#00e5ff);
    color:#000; font-size:11px; font-weight:800; padding:3px 12px;
    border-radius:20px; white-space:nowrap; letter-spacing:.4px;
  }
  .ps-plan-name { font-size:17px; font-weight:800; color:#fff; margin-bottom:4px; }
  .ps-price { font-size:36px; font-weight:900; color:#fff; margin:10px 0 2px; }
  .ps-price span { font-size:15px; font-weight:500; color:rgba(255,255,255,.45); }
  .ps-billing-toggle {
    display:flex; background:rgba(255,255,255,.06); border-radius:9px;
    padding:3px; gap:3px; margin:10px 0;
  }
  .ps-toggle-btn {
    flex:1; padding:7px 4px; border:none; border-radius:7px; font-size:12px;
    font-weight:700; cursor:pointer; transition:all .2s; font-family:inherit;
    color:rgba(255,255,255,.4); background:transparent;
  }
  .ps-toggle-btn.active { background:rgba(255,255,255,.12); color:#fff; }
  .ps-save-tag {
    display:inline-block; background:rgba(0,255,136,.1);
    border:1px solid rgba(0,255,136,.25); color:#00ff99;
    font-size:11px; font-weight:700; padding:2px 8px;
    border-radius:20px; margin-bottom:6px; align-self:flex-start;
  }
  .ps-trial-tag {
    font-size:12px; font-weight:700; color:#ffc800;
    margin-bottom:8px; letter-spacing:.2px;
  }
  .ps-features { display:flex; flex-direction:column; gap:8px; margin:14px 0 20px; flex:1; }
  .ps-feat { display:flex; align-items:center; gap:8px; font-size:13px; color:rgba(255,255,255,.75); }
  .ps-feat-check { font-size:14px; color:#00ff99; flex-shrink:0; }
  .ps-feat-lock { font-size:12px; color:rgba(255,255,255,.3); flex-shrink:0; }
  .ps-btn {
    width:100%; padding:14px; border:none; border-radius:12px; font-size:15px;
    font-weight:700; cursor:pointer; transition:.2s ease; font-family:inherit; margin-top:auto;
  }
  .ps-btn-primary {
    background:linear-gradient(135deg,#00ff99,#00e5ff); color:#000;
  }
  .ps-btn-primary:hover { transform:scale(1.02); }
  .ps-btn-secondary {
    background:rgba(255,255,255,.08); color:rgba(255,255,255,.75);
    border:1px solid rgba(255,255,255,.12);
  }
  .ps-btn-secondary:hover { background:rgba(255,255,255,.12); }
  .ps-btn:disabled { opacity:.5; cursor:not-allowed; transform:none; }
  .ps-logout {
    color:rgba(255,255,255,.3); font-size:13px; margin-top:20px;
    cursor:pointer; background:none; border:none; font-family:inherit;
  }
  .ps-logout:hover { color:rgba(255,255,255,.6); }
  .ps-team {
    width:100%; max-width:520px; margin-top:14px;
    background:rgba(255,255,255,.02); border:1px dashed rgba(255,255,255,.1);
    border-radius:16px; padding:16px 20px; display:flex;
    align-items:center; justify-content:space-between;
  }
  .ps-team-left { font-size:14px; font-weight:700; color:rgba(255,255,255,.5); }
  .ps-team-badge {
    font-size:11px; font-weight:700; background:rgba(255,255,255,.08);
    color:rgba(255,255,255,.4); padding:3px 10px; border-radius:20px;
  }
`;

function PlanSelectionScreen({ email, uid, onLogout, subStatus }) {
  const [loadingPlan, setLoadingPlan] = useState(null); // 'core' | 'pro'
  const [coreBilling, setCoreBilling] = useState('monthly');
  const [proBilling, setProBilling] = useState('monthly');
  const isCoreYearly = coreBilling === 'yearly';
  const isProYearly = proBilling === 'yearly';

  const handleSelect = async (plan) => {
    setLoadingPlan(plan);
    try {
      const billingCycle = plan === 'pro' ? proBilling : coreBilling;
      // Read referralCode from Firestore user doc (set during referral modal submit)
      let referralCode = '';
      try {
        const userData = await getUserData(uid);
        referralCode = userData?.referralCode || '';
      } catch {}
      await createCheckout(email, uid, plan, billingCycle, referralCode);
    } catch {
      setLoadingPlan(null);
    }
  };

  return (
    <>
      <style>{AUTH_CSS}{PLAN_CSS}</style>
      <div className="ps-container">
        <div className="ps-logo">TRACKSY</div>
        <div className="ps-tagline">Choose the plan that fits your hustle</div>

        {subStatus === 'canceled' && (
          <div className="ps-status-warn">Your subscription has ended. Pick a plan to continue.</div>
        )}
        {subStatus === 'past_due' && (
          <div className="ps-status-warn">Your payment is past due. Resubscribe to regain access.</div>
        )}

        <div className="ps-cards">
          {/* ── Core ── */}
          <div className="ps-card">
            <div className="ps-plan-name">Tracksy Core</div>
            <div className="ps-billing-toggle">
              <button
                className={`ps-toggle-btn${!isCoreYearly ? ' active' : ''}`}
                onClick={() => setCoreBilling('monthly')}
              >Monthly</button>
              <button
                className={`ps-toggle-btn${isCoreYearly ? ' active' : ''}`}
                onClick={() => setCoreBilling('yearly')}
              >Yearly</button>
            </div>
            {isCoreYearly && <div className="ps-save-tag">2 months free</div>}
            <div className="ps-price">
              {isCoreYearly ? <>$69.90<span>/yr</span></> : <>$6.99<span>/mo</span></>}
            </div>
            <div className="ps-trial-tag">🎉 7-day free trial</div>
            <div className="ps-features">
              <div className="ps-feat"><span className="ps-feat-check">✓</span> Sales tracking + commission calc</div>
              <div className="ps-feat"><span className="ps-feat-check">✓</span> AI coach — 50 msgs/month</div>
              <div className="ps-feat"><span className="ps-feat-check">✓</span> Voice memos — 10 max, 2 min</div>
              <div className="ps-feat"><span className="ps-feat-check">✓</span> Lead & activity tracking</div>
              <div className="ps-feat"><span className="ps-feat-check">✓</span> PDF export</div>
              <div className="ps-feat"><span className="ps-feat-check">✓</span> 2 months sales history</div>
              <div className="ps-feat"><span className="ps-feat-lock">✗</span> <span style={{color:'rgba(255,255,255,.35)'}}>Excel export, insights, win rate</span></div>
            </div>
            <button
              className="ps-btn ps-btn-secondary"
              onClick={() => handleSelect('core')}
              disabled={!!loadingPlan}
            >
              {loadingPlan === 'core' ? 'Redirecting...' : 'Start 7-Day Free Trial'}
            </button>
          </div>

          {/* ── Pro ── */}
          <div className="ps-card ps-featured">
            <div className="ps-card-badge">MOST POPULAR</div>
            <div className="ps-plan-name">Tracksy Pro</div>
            <div className="ps-billing-toggle">
              <button
                className={`ps-toggle-btn${!isProYearly ? ' active' : ''}`}
                onClick={() => setProBilling('monthly')}
              >Monthly</button>
              <button
                className={`ps-toggle-btn${isProYearly ? ' active' : ''}`}
                onClick={() => setProBilling('yearly')}
              >Yearly</button>
            </div>
            {isProYearly && <div className="ps-save-tag">2 months free</div>}
            <div className="ps-price">
              {isProYearly ? <>$99.90<span>/yr</span></> : <>$9.99<span>/mo</span></>}
            </div>
            <div className="ps-trial-tag">🚀 14-day free trial</div>
            <div className="ps-features">
              <div className="ps-feat"><span className="ps-feat-check">✓</span> Everything in Core</div>
              <div className="ps-feat"><span className="ps-feat-check">✓</span> AI coach — 200 msgs/month</div>
              <div className="ps-feat"><span className="ps-feat-check">✓</span> Voice memos — 25 max, unlimited length</div>
              <div className="ps-feat"><span className="ps-feat-check">✓</span> Auto transcription + download</div>
              <div className="ps-feat"><span className="ps-feat-check">✓</span> Unlimited sales history</div>
              <div className="ps-feat"><span className="ps-feat-check">✓</span> Excel export + AI insights report</div>
              <div className="ps-feat"><span className="ps-feat-check">✓</span> Win rate card + performance trend</div>
            </div>
            <button
              className="ps-btn ps-btn-primary"
              onClick={() => handleSelect('pro')}
              disabled={!!loadingPlan}
            >
              {loadingPlan === 'pro' ? 'Redirecting...' : 'Start 14-Day Free Trial'}
            </button>
          </div>
        </div>

        {/* Team — coming soon */}
        <div className="ps-team">
          <div className="ps-team-left">Tracksy Team — Multi-rep dashboard & manager view</div>
          <div className="ps-team-badge">Coming Soon</div>
        </div>

        <button className="ps-logout" onClick={onLogout}>Sign out</button>
      </div>
    </>
  );
}

// ─── REFERRAL MODAL ──────────────────────────────────────────────────────────
// initialCode: pre-filled from ?ref= URL capture or typed on auth screen
// onSubmit: returns false if code is invalid (modal stays open, shows error)
function ReferralModal({ onSubmit, onSkip, initialCode = '' }) {
  const [code, setCode] = useState(initialCode);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError('');
    const result = await onSubmit(trimmed);
    if (result === false) {
      setError("That referral code doesn't exist. Please check and try again.");
      setSubmitting(false);
    }
    // If result !== false (success), parent closes modal — no need to setSubmitting(false)
  };

  return (
    <div className="ref-modal-overlay" onClick={onSkip}>
      <div className="ref-modal" onClick={function(e) { e.stopPropagation(); }}>
        <div className="ref-modal-emoji">🤝</div>
        <h3>Were you referred by someone?</h3>
        {initialCode && (
          <div className="ref-modal-applied">
            Referral code applied: {initialCode}
          </div>
        )}
        <p>If someone shared Tracksy with you, enter their referral code below to give them credit!</p>
        {error && <div className="ref-modal-error">{error}</div>}
        <input
          type="text"
          placeholder="Referral code (optional)"
          value={code}
          onChange={function(e) { setCode(e.target.value.toUpperCase()); setError(''); }}
          autoComplete="off"
          autoFocus
        />
        <div className="ref-modal-btns">
          <button className="ref-modal-skip" onClick={onSkip} disabled={submitting}>Skip</button>
          <button className="ref-modal-submit" onClick={handleSubmit} disabled={!code.trim() || submitting}>
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── APP WRAPPER ────────────────────────────────────────────────────────────
function AppWrapper() {
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [appData, setAppData] = useState(null);
  const [subscription, setSubscription] = useState(null); // null = checking, object = result
  const [subChecked, setSubChecked] = useState(false);
  const [isExempt, setIsExempt] = useState(false);
  const [exemptChecked, setExemptChecked] = useState(false);
  const [showRefModal, setShowRefModal] = useState(false);
  const refModalUser = useRef(null); // store uid/email for the modal's submit handler
  const refModalPrefilledCode = useRef('');
  const saveTimeout = useRef(null);
  const isInitialLoad = useRef(true);
  const lastSavedJSON = useRef('');
  const userLoaded = useRef(false);
  const userRef = useRef(null); // Stable ref for user UID — avoids closure issues

  // Check subscription status
  const checkSub = useCallback(async (email, uid) => {
    // If we just returned from checkout, don't use stale cache
    const params = new URLSearchParams(window.location.search);
    const justCheckedOut = params.get('session_id');
    
    if (!justCheckedOut) {
      // Use cache first (valid for 5 minutes)
      const cached = getSubCache(uid);
      if (cached && Date.now() - cached.cachedAt < 5 * 60 * 1000) {
        setSubscription(cached);
        setSubChecked(true);
        return;
      }
    }
    
    const sub = await checkSubscription(email);
    if (sub.status !== 'error') {
      setSubscription(sub);
      setSubCache(uid, sub);
      // Sync Stripe's authoritative planKey to Firestore (recovery for missed webhooks)
      if (sub.active && sub.planKey) {
        setPlanInFirestore(uid, sub.planKey, {}).catch(() => {});
      }
    }
    setSubChecked(true);
  }, []);

  // Check whether the logged-in email is in the admin exemption list (config/exemptions)
  const checkExemption = useCallback(async (email) => {
    try {
      const emails = await getExemptEmails();
      setIsExempt(emails.map(e => e.toLowerCase()).includes(email.toLowerCase()));
    } catch {
      // If Firestore is unreachable, leave isExempt false — normal paywall applies
    } finally {
      setExemptChecked(true);
    }
  }, []);

  // Handle successful checkout return
  const sessionHandled = useRef(false);
  const hasSessionId = useRef(!!new URLSearchParams(window.location.search).get('session_id'));
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const canceled = params.get('canceled');
    
    if (canceled) {
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    
    if (sessionId && !sessionHandled.current && user) {
      sessionHandled.current = true;
      // Clear URL params immediately
      window.history.replaceState({}, '', window.location.pathname);
      
      // Clear stale sub cache for this user
      if (user?.uid) {
        try { localStorage.removeItem(`tracksy_sub_${user.uid}`); } catch(e) {}
      }
      
      // Immediately grant access — Stripe redirected back so checkout succeeded
      const optimisticSub = { status: 'trialing', active: true };
      setSubscription(optimisticSub);
      setSubChecked(true);
      
      // Verify with Stripe in background, set plan in Firestore, update cache
      (async () => {
        try {
          // Get targetPlan from session metadata and write to Firestore immediately
          // This works even if the webhook hasn't fired yet
          const sessionRes = await fetch('/api/stripe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'verify-session', sessionId }),
          });
          const sessionData = await sessionRes.json();
          if (sessionData.targetPlan && user?.uid) {
            await setPlanInFirestore(user.uid, sessionData.targetPlan, {});
          }

          await new Promise(r => setTimeout(r, 2000));

          if (user?.email && user?.uid) {
            const sub = await checkSubscription(user.email);
            if (sub.active) {
              setSubscription(sub);
              setSubCache(user.uid, sub);
            } else {
              setSubCache(user.uid, optimisticSub);
            }
          }
        } catch(e) {
          console.log('Post-checkout verify error:', e);
        }
      })();
    }
  }, [user]);

  // On mount: check if already logged in (returning user / page refresh)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser && !userLoaded.current) {
        userLoaded.current = true;
        const uid = firebaseUser.uid;
        const cached = getCache(uid);
        
        // Show cached data immediately for fast load
        setAppData(cached || getDefaultData(firebaseUser.displayName || ''));
        // Store stable plain object — NOT the Firebase User object (which changes on token refresh)
        setUser({ uid: firebaseUser.uid, email: firebaseUser.email, displayName: firebaseUser.displayName });
        userRef.current = uid;
        lastSavedJSON.current = JSON.stringify(cached || '');

        // Check subscription (non-blocking) — but skip if returning from checkout
        // The session_id handler will deal with subscription in that case
        if (!hasSessionId.current) {
          checkSub(firebaseUser.email, uid);
        }
        // Exemption check always runs regardless of checkout status
        checkExemption(firebaseUser.email);

        // Fetch from Firestore for cross-device sync — but only apply on initial load
        // After app is interactive, the app's own state is source of truth
        Promise.all([getUserData(uid), getVoiceMemos(uid)]).then(([data, memos]) => {
          if (data) {
            // Cache and dedup-ref NEVER include memos (prevents localStorage bloat)
            setCache(uid, data);
            lastSavedJSON.current = JSON.stringify(data);
            if (isInitialLoad.current) {
              setAppData({ ...data, voiceMemos: memos });
            }
          } else if (!cached) {
            const d = getDefaultData(firebaseUser.displayName || '');
            saveUserData(uid, { ...d, createdAt: new Date().toISOString() });
            setCache(uid, d);
          }
          setTimeout(() => { isInitialLoad.current = false; }, 500);
        }).catch((err) => { console.error('Firestore sync failed on session restore:', err); setTimeout(() => { isInitialLoad.current = false; }, 500); });
      } else if (!firebaseUser) {
        userLoaded.current = false;
        userRef.current = null;
        setUser(null); setAppData(null);
      }
      setCheckingAuth(false);
    });
    return () => unsubscribe();
  }, []);

  // Called directly from AuthScreen — sets userLoaded so onAuthStateChanged won't re-run
  const handleLogin = (firebaseUser, manualRefCode) => {
    userLoaded.current = true;
    const uid = firebaseUser.uid;
    const cached = getCache(uid);
    
    // Show cached data immediately
    setAppData(cached || getDefaultData(firebaseUser.displayName || ''));
    // Store stable plain object — NOT the Firebase User object (which changes on token refresh)
    setUser({ uid: firebaseUser.uid, email: firebaseUser.email, displayName: firebaseUser.displayName });
    userRef.current = uid;
    isInitialLoad.current = true;

    // Check subscription and exemption (non-blocking)
    checkSub(firebaseUser.email, uid);
    checkExemption(firebaseUser.email);

    // ALWAYS sync from Firestore for cross-device data
    Promise.all([getUserData(uid), getVoiceMemos(uid)]).then(([data, memos]) => {
      if (data) {
        // Cache and dedup-ref NEVER include memos (prevents localStorage bloat)
        setCache(uid, data);
        lastSavedJSON.current = JSON.stringify(data);
        setAppData({ ...data, voiceMemos: memos });
      } else {
        // Brand new user — create doc immediately, then show referral modal
        const d = getDefaultData(firebaseUser.displayName || '');
        saveUserData(uid, { ...d, createdAt: new Date().toISOString() });
        setCache(uid, d);
        // Always show modal — pre-filled if URL code captured or code typed on auth screen
        const storedCode = getStoredReferralCode();
        const prefilledCode = manualRefCode || storedCode || '';
        refModalUser.current = { uid: firebaseUser.uid, email: firebaseUser.email };
        refModalPrefilledCode.current = prefilledCode;
        setShowRefModal(true);
      }
      setTimeout(() => { isInitialLoad.current = false; }, 500);
    }).catch((err) => { console.error('Firestore sync failed on login:', err); setTimeout(() => { isInitialLoad.current = false; }, 500); });
  };

  // Debounced save — uses userRef to avoid stale closure on user state
  const handleDataChange = useCallback((data) => {
    const uid = userRef.current;
    if (!uid || isInitialLoad.current) return;
    const json = JSON.stringify(data);
    if (json === lastSavedJSON.current) return;
    setCache(uid, data);
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      try { await saveUserData(uid, data); lastSavedJSON.current = json; }
      catch (err) { console.error('Save error:', err); }
    }, 1000);
  }, []);

  const handleLogout = async () => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    try { await logout(); } catch(e) {}
    userRef.current = null;
    userLoaded.current = false;
    setUser(null); setAppData(null); setSubscription(null); setSubChecked(false);
    setIsExempt(false); setExemptChecked(false);
  };

  if (checkingAuth) {
    return <><style>{AUTH_CSS}</style><div className="auth-loading"><div className="auth-spinner" /></div></>;
  }

  if (!user) {
    return <AuthScreen onLogin={handleLogin} />;
  }

  // ── Referral modal MUST block before paywall/Stripe redirect ──
  // New Google users with no referral code get asked here, before subscription check
  const handleRefModalSubmit = async (code) => {
    const info = refModalUser.current;
    if (!info) { refModalUser.current = null; refModalPrefilledCode.current = ''; setShowRefModal(false); return true; }

    // Validate code first (server-side API — always works regardless of Firestore rules)
    const affiliate = await validateAffiliateCode(code);
    if (!affiliate) return false; // Invalid code — keep modal open with error

    // Save referralCode + affiliateId to user doc so Stripe webhook can read it
    try {
      await saveUserData(info.uid, {
        referralCode: code.toUpperCase(),
        affiliateId: affiliate.id,
      });
    } catch {}

    // Record referral doc — non-fatal: Firestore write may fail if rules not yet deployed;
    // Stripe webhook is the source of truth for commission logic
    try {
      await recordReferral(info.uid, info.email, code);
    } catch (err) {
      console.warn('[referral] recordReferral write failed (non-fatal):', err.message);
    }

    refModalUser.current = null;
    refModalPrefilledCode.current = '';
    setShowRefModal(false);
    return true;
  };

  const handleRefModalSkip = function() {
    refModalUser.current = null;
    setShowRefModal(false);
  };

  if (showRefModal) {
    return (
      <>
        <style>{AUTH_CSS}</style>
        <ReferralModal
          onSubmit={handleRefModalSubmit}
          onSkip={handleRefModalSkip}
          initialCode={refModalPrefilledCode.current}
        />
      </>
    );
  }

  // Wait for both subscription and exemption checks before rendering
  if (!subChecked || !exemptChecked) {
    return <><style>{AUTH_CSS}</style><div className="auth-loading"><div className="auth-spinner" /><div style={{color:'rgba(255,255,255,.5)',fontSize:14}}>Checking subscription...</div></div></>;
  }

  // Show plan selection if no active subscription — exempt/whitelisted users bypass
  if (!isExempt && subscription && !subscription.active && subscription.status !== 'error') {
    return <PlanSelectionScreen email={user.email} uid={user.uid} onLogout={handleLogout} subStatus={subscription.status} />;
  }

  return (
    <TracksyApp
      key={user.uid}
      initialUserData={appData?.settings || undefined}
      initialSales={appData?.sales || []}
      initialActivities={appData?.activities || []}
      initialBonusTiers={appData?.bonusTiers || undefined}
      initialVoiceMemos={appData?.voiceMemos || []}
      initialLeads={appData?.leads || []}
      initialAppointments={appData?.appointments || []}
      onDataChange={handleDataChange}
      onLogout={handleLogout}
      userEmail={user.email || ''}
      userUid={user.uid || ''}
    />
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <>
    <AppRouter>
      <AppWrapper />
    </AppRouter>
    <Analytics />
  </>
);
